import {KpisList, type MtmYtmRequestPayload} from '@domain/kpis'
import {HierarchyNodeType} from '@domain/shared'
import {Iso8601, TimeRange} from '@hconnect/common/types'
import {getSystemTimezone} from '@hconnect/common/utils'
import {AxiosError} from 'axios'
import {isEmpty} from 'lodash'
import {useMemo} from 'react'

import {attachQuarterlyData} from '../mappers'
import {AggregatedKpiData, KpiData} from '../types'
import {type KpiHookReturn} from '../types'
import {checkIfQuarterlyDataExists, showYtmData} from '../utils'

import {
  useAlternativeFuelRatesData,
  useKilnCoefficientsData,
  useKilnHeatConsumptionData,
  useMeanTimeBetweenFailuresData,
  useProductionPerformanceValues,
  useTechnicalCementPowerConsumptionData
} from './api'
import {useKpiQuarterlyValue} from './useKpiQuarterlyValue'

// clinker and cement hooks use different amount of nested hooks than the rest of kpis
// so to keep rendered hooks consistency both should be engaged at the same time
const TAB_API_MAPPING = {
  [KpisList.KilnReliabilityCoefficient]: useKilnCoefficientsData,
  [KpisList.KilnOperatingCoefficient]: useKilnCoefficientsData,
  [KpisList.KilnHeatConsumption]: useKilnHeatConsumptionData,
  [KpisList.AlternativeFuelRate]: useAlternativeFuelRatesData,
  [KpisList.MTBF]: useMeanTimeBetweenFailuresData,
  [KpisList.TechnicalCementPowerCons]: useTechnicalCementPowerConsumptionData
}

type AggregatedKpiValue = {
  data?: AggregatedKpiData
  isInitialLoading: boolean
  error: AxiosError | null
  refetch: () => void
  hasQuarterlyData: boolean
}

type UseKpiValueParams = {
  currentTab: KpisList
  from: Iso8601
  to: Iso8601
  quickSelectionId?: string | null
  locationId?: string
}

export const useKpiValue = ({
  currentTab,
  from,
  to,
  quickSelectionId,
  locationId
}: UseKpiValueParams): AggregatedKpiValue => {
  const timeRange: TimeRange = {startDate: from, endDate: to}
  // Note: we use the upper bound of the date range for the timestamp
  const timestamp = to
  const useApiHook = useMemo<
    (payload: MtmYtmRequestPayload, enabled?: boolean) => KpiHookReturn
  >(() => {
    return TAB_API_MAPPING[currentTab] || useKilnCoefficientsData
  }, [currentTab])

  const isCementClinkerTab =
    currentTab === KpisList.ClinkerProduction || currentTab === KpisList.CementProduction

  const {
    data: kpiData,
    isInitialLoading: kpiLoading,
    error: kpiError,
    refetch: kpiRefetch
  } = useApiHook(
    {
      timezone: getSystemTimezone(),
      timestamp,
      locationId,
      showYTM: showYtmData(quickSelectionId)
    },
    !isCementClinkerTab
  )
  const {
    data: productionPerformanceData,
    isInitialLoading: productionPerformanceLoading,
    error: productionPerformanceError,
    refetch: productionPerformanceRefetch
  } = useProductionPerformanceValues(
    {
      timezone: getSystemTimezone(),
      timeRange,
      locationId,
      showYTM: showYtmData(quickSelectionId)
    },
    {enabled: isCementClinkerTab}
  )

  const {
    data: kpiQuarterlyData,
    isInitialLoading: isInitialLoadingQuarterly,
    error: errorQuarterly,
    refetch: refetchQuarterly
  } = useKpiQuarterlyValue({
    timestamp,
    currentTab,
    timezone: getSystemTimezone(),
    locationId: locationId || HierarchyNodeType.Global
  })

  return useMemo(() => {
    const data = isCementClinkerTab ? (productionPerformanceData as KpiData) : kpiData
    const isInitialLoading = isCementClinkerTab ? productionPerformanceLoading : kpiLoading
    const error = isCementClinkerTab ? productionPerformanceError : kpiError
    const refetch = isCementClinkerTab ? productionPerformanceRefetch : kpiRefetch

    const hasQuarterlyData =
      !isEmpty(kpiQuarterlyData) &&
      checkIfQuarterlyDataExists(currentTab, kpiQuarterlyData, locationId)
    const aggregatedData: AggregatedKpiData | null = attachQuarterlyData(
      currentTab,
      data,
      kpiQuarterlyData || undefined
    )

    return {
      data: aggregatedData || data,
      isInitialLoading: isInitialLoading || isInitialLoadingQuarterly,
      error: error || errorQuarterly,
      refetch: () => {
        const refetchPromises = [refetch(), refetchQuarterly()]
        void Promise.all(refetchPromises)
      },
      hasQuarterlyData
    }
  }, [
    currentTab,
    isCementClinkerTab,
    kpiData,
    kpiError,
    kpiLoading,
    kpiRefetch,
    productionPerformanceData,
    productionPerformanceError,
    productionPerformanceLoading,
    productionPerformanceRefetch,
    kpiQuarterlyData,
    isInitialLoadingQuarterly,
    errorQuarterly,
    refetchQuarterly,
    locationId
  ])
}
